import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Trash2 } from 'lucide-react';
import { DataTableColumnHeader } from '@/components/core';
import { formatDate } from '@/lib/utils/custom-date/custom-date';
import { Button } from '@/components/ui-kit/button';
import { Location } from '../../types/location.types';

interface CreateLocationTableColumnsProps {
  t: (key: string) => string;
  onEdit: (location: Location) => void;
  onDelete: (location: Location) => void;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export const createLocationTableColumns = ({
  t,
  onEdit,
  onDelete,
  canUpdate = true,
  canDelete = true,
}: CreateLocationTableColumnsProps): ColumnDef<Location>[] => [
  {
    id: 'State',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LOCATION_STATE')} />,
    accessorFn: (row) => row.State ?? '',
    cell: ({ row }) => <span className="truncate font-medium">{row.original.State}</span>,
  },
  {
    id: 'CreatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CREATION_DATE')} />,
    accessorFn: (row) => row.CreatedDate ?? '',
    cell: ({ row }) =>
      row.original.CreatedDate ? formatDate(new Date(row.original.CreatedDate), true) : '-',
  },
  {
    id: 'LastUpdatedDate',
    meta: { filter: { type: 'datetime' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('LAST_MODIFIED')} />,
    accessorFn: (row) => row.LastUpdatedDate ?? '',
    cell: ({ row }) =>
      row.original.LastUpdatedDate ? formatDate(new Date(row.original.LastUpdatedDate), true) : '-',
  },
  ...(canUpdate || canDelete
    ? [
        {
          id: 'actions',
          // Card: top right, as the old portal's card header.
          meta: { mobile: 'badge' as const },
          header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }: { row: { original: Location } }) => (
            <div className="flex items-center gap-1">
              {canUpdate && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onEdit(row.original)}
                  aria-label={t('EDIT')}
                >
                  <Pencil className="size-4" />
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(row.original)}
                  aria-label={t('DELETE')}
                >
                  <Trash2 className="size-4" />
                </Button>
              )}
            </div>
          ),
        },
      ]
    : []),
];
